import { createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@store/index';
import { LAYOUT } from '../../constants/layout';

export type SidebarMode = 'mini' | 'expanded' | 'hidden';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
  category?: 'session' | 'general';
}

interface UIState {
  sidebarMode: SidebarMode;
  chatOpen: boolean;
  chatWidth: number | null;  // null = use responsive default
  theme: 'light' | 'dark';
  language: 'ja' | 'en';
  notifications: Notification[];
  modals: {
    uploadDocument: boolean;
    documentDetails: boolean;
    shareDocument: boolean;
    userProfile: boolean;
  };
}

const getInitialSidebarMode = (): SidebarMode => {
  const stored = localStorage.getItem('sidebarMode');
  if (stored === 'mini' || stored === 'expanded' || stored === 'hidden') return stored;
  return 'mini';
};

const getInitialChatOpen = (): boolean => {
  const stored = localStorage.getItem('chatOpen');
  if (stored !== null) return stored === 'true';
  return true;
};

const getInitialChatWidth = (): number | null => {
  const stored = localStorage.getItem('chatWidth');
  if (stored) {
    const n = parseInt(stored, 10);
    if (n >= 280 && n <= 800) return n;
  }
  return null;
};

const initialState: UIState = {
  sidebarMode: getInitialSidebarMode(),
  chatOpen: getInitialChatOpen(),
  chatWidth: getInitialChatWidth(),
  theme: localStorage.getItem('theme') as 'light' | 'dark' || 'light',
  language: localStorage.getItem('language') as 'ja' | 'en' || 'ja',
  notifications: [],
  modals: {
    uploadDocument: false,
    documentDetails: false,
    shareDocument: false,
    userProfile: false,
  },
};

// Slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Legacy actions — rewired to sidebarMode for backward compatibility
    toggleSidebar: (state) => {
      const next = state.sidebarMode === 'expanded' ? 'mini' : 'expanded';
      state.sidebarMode = next;
      localStorage.setItem('sidebarMode', next);
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      const next = action.payload ? 'expanded' : 'mini';
      state.sidebarMode = next;
      localStorage.setItem('sidebarMode', next);
    },
    setSidebarMode: (state, action: PayloadAction<SidebarMode>) => {
      state.sidebarMode = action.payload;
      localStorage.setItem('sidebarMode', action.payload);
    },
    toggleSidebarExpand: (state) => {
      const next = state.sidebarMode === 'expanded' ? 'mini' : 'expanded';
      state.sidebarMode = next;
      localStorage.setItem('sidebarMode', next);
    },
    setChatOpen: (state, action: PayloadAction<boolean>) => {
      state.chatOpen = action.payload;
      localStorage.setItem('chatOpen', String(action.payload));
    },
    toggleChat: (state) => {
      state.chatOpen = !state.chatOpen;
      localStorage.setItem('chatOpen', String(state.chatOpen));
    },
    setChatWidth: (state, action: PayloadAction<number>) => {
      const w = Math.max(280, Math.min(800, action.payload));
      state.chatWidth = w;
      localStorage.setItem('chatWidth', String(w));
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
      localStorage.setItem('theme', action.payload);
    },
    setLanguage: (state, action: PayloadAction<'ja' | 'en'>) => {
      state.language = action.payload;
      localStorage.setItem('language', action.payload);
    },
    showNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: nanoid(),
        duration: action.payload.duration || 5000,
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    clearSessionNotifications: (state) => {
      state.notifications = state.notifications.filter(n => n.category !== 'session');
    },
    openModal: (state, action: PayloadAction<keyof UIState['modals']>) => {
      state.modals[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof UIState['modals']>) => {
      state.modals[action.payload] = false;
    },
    closeAllModals: (state) => {
      Object.keys(state.modals).forEach(key => {
        state.modals[key as keyof UIState['modals']] = false;
      });
    },
  },
});

// Actions
export const {
  toggleSidebar,
  setSidebarOpen,
  setSidebarMode,
  toggleSidebarExpand,
  setChatOpen,
  setChatWidth,
  toggleChat,
  setTheme,
  setLanguage,
  showNotification,
  removeNotification,
  clearNotifications,
  clearSessionNotifications,
  openModal,
  closeModal,
  closeAllModals,
} = uiSlice.actions;

// Selectors
export const selectSidebarOpen = (state: RootState) => state.ui.sidebarMode === 'expanded';
export const selectSidebarMode = (state: RootState) => state.ui.sidebarMode;
export const selectSidebarWidth = (state: RootState) => {
  switch (state.ui.sidebarMode) {
    case 'expanded': return LAYOUT.SIDEBAR_WIDTH_EXPANDED;
    case 'mini': return LAYOUT.SIDEBAR_WIDTH_MINI;
    case 'hidden': return 0;
  }
};
export const selectChatOpen = (state: RootState) => state.ui.chatOpen;
export const selectChatWidth = (state: RootState) => state.ui.chatWidth;
export const selectTheme = (state: RootState) => state.ui.theme;
export const selectLanguage = (state: RootState) => state.ui.language;
export const selectNotifications = (state: RootState) => state.ui.notifications;
export const selectModals = (state: RootState) => state.ui.modals;

export default uiSlice.reducer;
